import { matchPath } from 'react-router-dom';
import { FinancialAidRoutes } from '../../financial-aid/routes/FinancialAidRoutes';
import financialAidComponents from '../../../../../config/semantic-components/financial-aid-intellio-v2.json';
import financialAidRoutesConfig from '../../../../../config/semantic-routes/financial-aid.json';
import {
  SemanticRouteAdapter,
  type SemanticComponentsConfig,
  type SemanticRoutesConfig,
} from '../SemanticRouteAdapter';

const componentsConfig = financialAidComponents as SemanticComponentsConfig;
const routesConfig = financialAidRoutesConfig as SemanticRoutesConfig;

const FINANCIAL_AID_DETAIL_TABS = new Set([
  'family-profile',
  'income-calculation',
  'assets-equity',
  'expenses-spending',
  'contract-flags',
  'corrections',
  'documents',
  'notes',
]);

const normalizeDetailTab = (value?: string): string | null => {
  const normalized = value?.trim().toLowerCase() ?? '';
  return FINANCIAL_AID_DETAIL_TABS.has(normalized) ? normalized : null;
};

const normalizeWildcard = (value?: string): string => value?.replace(/^\/+|\/+$/g, '') ?? '';

const resolveStaffDetailFromWildcard = (
  wildcard: string,
): { applicationId: string; tab: string | null } | null => {
  const staffMatch = wildcard.match(/^staff\/([^/]+)(?:\/([^/]+))?$/);
  if (staffMatch?.[1]) {
    return {
      applicationId: staffMatch[1],
      tab: normalizeDetailTab(staffMatch[2]),
    };
  }
  const aliasMatch = wildcard.match(/^application\/([^/]+)(?:\/([^/]+))?$/);
  if (aliasMatch?.[1]) {
    return {
      applicationId: aliasMatch[1],
      tab: normalizeDetailTab(aliasMatch[2]),
    };
  }
  return null;
};

const resolveInternalPath = (
  componentId: string,
  params: Record<string, string | undefined>,
): string | null => {
  if (componentId === 'fa-parent-apply') return '/school/financialaid/apply';
  if (componentId === 'fa-parent-status') return '/school/financialaid/status';
  if (componentId === 'fa-staff-dashboard') return '/school/financialaid/staff';
  if (componentId === 'fa-staff-help') return '/school/financialaid/help';
  if (componentId === 'fa-staff-reports') return '/school/financialaid/staff/reports';
  if (componentId === 'fa-staff-applicant-detail') {
    const wildcard = normalizeWildcard(params['*']);
    const wildcardDetail = resolveStaffDetailFromWildcard(wildcard);
    if (wildcardDetail?.tab) {
      return `/school/financialaid/staff/${wildcardDetail.applicationId}/${wildcardDetail.tab}`;
    }
    if (wildcardDetail?.applicationId) {
      return `/school/financialaid/staff/${wildcardDetail.applicationId}/family-profile`;
    }

    const applicationId = params.applicationId ?? params.familyId;
    const detailTab = normalizeDetailTab(params.tab);
    if (!applicationId) {
      return null;
    }
    if (detailTab) {
      return `/school/financialaid/staff/${applicationId}/${detailTab}`;
    }
    return `/school/financialaid/staff/${applicationId}/family-profile`;
  }
  return null;
};

const mapCanonicalFinancialAidPath = (pathname: string | null): string | null => {
  if (!pathname) return pathname;
  const normalized = pathname.replace(/\/+$/, '') || '/';

  if (normalized === '/school/financialaid') {
    return '/admin/financial-aid/staff/dashboard';
  }
  if (normalized === '/school/financialaid/dashboard') {
    return '/admin/financial-aid/staff/dashboard';
  }
  if (normalized === '/school/financialaid/staff') {
    return '/admin/financial-aid/staff/dashboard';
  }
  if (normalized === '/school/financialaid/staff/dashboard') {
    return '/admin/financial-aid/staff/dashboard';
  }
  if (normalized === '/school/financialaid/help') {
    return '/admin/financial-aid/staff/help';
  }

  const staffDetail = matchPath({ path: '/school/financialaid/staff/:applicationId', end: true }, normalized);
  if (staffDetail?.params.applicationId) {
    const applicationId = staffDetail.params.applicationId;
    if (applicationId !== 'reports' && applicationId !== 'appeals') {
      return `/admin/financial-aid/staff/applicant/${applicationId}`;
    }
    return '/admin/financial-aid/staff/dashboard';
  }

  const staffDetailTab = matchPath(
    { path: '/school/financialaid/staff/:applicationId/:tab', end: true },
    normalized,
  );
  if (staffDetailTab?.params.applicationId) {
    const applicationId = staffDetailTab.params.applicationId;
    if (applicationId !== 'reports' && applicationId !== 'appeals') {
      return `/admin/financial-aid/staff/applicant/${applicationId}`;
    }
    return '/admin/financial-aid/staff/dashboard';
  }

  const detail = matchPath({ path: '/school/financialaid/application/:familyId', end: true }, normalized);
  if (detail?.params.familyId) {
    return `/admin/financial-aid/staff/applicant/${detail.params.familyId}`;
  }

  const detailTab = matchPath(
    { path: '/school/financialaid/application/:familyId/:tab', end: true },
    normalized,
  );
  if (detailTab?.params.familyId) {
    return `/admin/financial-aid/staff/applicant/${detailTab.params.familyId}`;
  }

  return pathname;
};

export function FinancialAidSemanticRoute() {
  return (
    <SemanticRouteAdapter
      moduleKey="financial-aid"
      componentsConfig={componentsConfig}
      routesConfig={routesConfig}
      mapIntegrationPath={mapCanonicalFinancialAidPath}
      strictNoFallback
      blockBridgedTarget
      internal={{
        routes: FinancialAidRoutes,
        resolvePath: resolveInternalPath,
      }}
    />
  );
}
